/**
 * @file Customer service — CRUD operations on customers store (pbf_customers)
 * Compatible with Deploy Z customer data model for bidirectional sync.
 */

import { store, load } from '@state/store.js';

// =============================================
// CRUD OPERATIONS
// =============================================

/**
 * Load all customers from store.
 */
export function getCustomers() {
  return load('customers', []).filter(c => !c.archived);
}

/**
 * Load ALL customers including archived.
 */
export function getAllCustomers() {
  return load('customers', []);
}

/**
 * Get a single customer by ID.
 */
export function getCustomerById(id) {
  const customers = getAllCustomers();
  return customers.find(c => c.id === id) || null;
}

/**
 * Add a new customer (Deploy Z compatible structure).
 */
export function addCustomer(data) {
  const customers = getAllCustomers();
  const id = 'cust_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
  const nameParts = (data.name || '').split(' ');
  const newCustomer = {
    id,
    name: data.name || '',
    firstName: data.firstName || nameParts[0] || '',
    lastName: data.lastName || nameParts.slice(1).join(' ') || '',
    phone: data.phone || '',
    email: data.email || '',
    address: data.address || '',
    source: data.source || '',
    type: data.type || '',
    companyName: data.companyName || '',
    billingAddress: data.billingAddress || '',
    pipelineStage: data.pipelineStage || '',
    lastContacted: '',
    balance: 0,
    tags: data.tags || [],
    activities: [],
    contactNotes: data.notes ? [{ id: Date.now().toString(36), text: data.notes, date: new Date().toISOString() }] : [],
    contactTasks: [],
    attachments: [],
    jobs: [],
    driveFiles: [],
    phones: data.phone ? [{ id: Date.now().toString(36), label: 'Cell', number: data.phone }] : [],
    properties: data.address ? [{ id: Date.now().toString(36), name: 'Primary', address: data.address, notes: '' }] : [],
    created: new Date().toISOString(),
    archived: false,
  };
  customers.push(newCustomer);
  store('customers', customers);
  return newCustomer;
}

/**
 * Update a customer with partial data.
 */
export function updateCustomer(id, updates) {
  const customers = getAllCustomers();
  const idx = customers.findIndex(c => c.id === id);
  if (idx === -1) return null;
  // If name is updated, also update firstName/lastName
  if (updates.name && !updates.firstName) {
    const parts = updates.name.split(' ');
    updates.firstName = parts[0] || '';
    updates.lastName = parts.slice(1).join(' ') || '';
  }
  customers[idx] = { ...customers[idx], ...updates };
  store('customers', customers);
  return customers[idx];
}

/**
 * Search customers by query string (name, phone, email, address).
 */
export function searchCustomers(query) {
  if (!query) return getCustomers();
  const q = query.toLowerCase();
  return getCustomers().filter(c =>
    (c.name || '').toLowerCase().includes(q) ||
    (c.firstName || '').toLowerCase().includes(q) ||
    (c.lastName || '').toLowerCase().includes(q) ||
    (c.phone || '').toLowerCase().includes(q) ||
    (c.email || '').toLowerCase().includes(q) ||
    (c.address || '').toLowerCase().includes(q) ||
    (c.companyName || '').toLowerCase().includes(q)
  );
}

/**
 * Get all jobs for a customer (searches both pipelines).
 */
export function getCustomerJobs(customerId) {
  const pipeline = load('pipeline', {});
  const jobsPipeline = load('jobs_pipeline', {});
  const jobs = [];

  for (const stageKey of Object.keys(pipeline)) {
    const cards = pipeline[stageKey];
    if (!Array.isArray(cards)) continue;
    for (const card of cards) {
      if (card.customerId === customerId) {
        jobs.push({ ...card, stage: stageKey, pipelineType: 'sales' });
      }
    }
  }
  for (const stageKey of Object.keys(jobsPipeline)) {
    const cards = jobsPipeline[stageKey];
    if (!Array.isArray(cards)) continue;
    for (const card of cards) {
      if (card.customerId === customerId) {
        jobs.push({ ...card, stage: stageKey, pipelineType: 'jobs' });
      }
    }
  }
  return jobs;
}

/**
 * Get total revenue for a customer from saved invoices.
 */
export function getCustomerRevenue(customerId) {
  const invoices = load('saved_invoices', []);
  return invoices
    .filter(inv => inv.customerId === customerId && (inv.status === 'paid' || inv.status === 'sent'))
    .reduce((sum, inv) => sum + (inv.total || 0), 0);
}

/**
 * Add a timestamped note to a customer.
 */
export function addCustomerNote(id, text) {
  const customers = getAllCustomers();
  const cust = customers.find(c => c.id === id);
  if (!cust) return null;
  if (!cust.contactNotes) cust.contactNotes = [];
  cust.contactNotes.push({
    id: Date.now().toString(36),
    text,
    date: new Date().toISOString(),
  });
  cust.lastContacted = new Date().toISOString();
  if (!cust.activities) cust.activities = [];
  cust.activities.push({
    type: 'note',
    text: text.substring(0, 100),
    date: new Date().toISOString(),
  });
  store('customers', customers);
  return cust;
}

/**
 * Format a relative time string.
 */
export function timeAgo(dateStr) {
  if (!dateStr) return 'never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
